import type { WhatPeopleSay, WhatPeopleSayInsight, WhatPeopleSayMention } from '../../../model/whatPeopleSay/whatPeopleSay';
import type { GetProductReviewInsightResponse, ReviewInsightMention } from './types';

/** Excerpts arrive unquoted from the API; the design renders them in curly quotes. */
const toQuote = (excerpt: string): string => `“${excerpt}”`;

const toMention = (
  mention: ReviewInsightMention,
  sentiment: WhatPeopleSayMention['sentiment'],
): WhatPeopleSayMention => ({
  id: mention.id,
  label: mention.label,
  count: mention.count,
  // The server resolves the tier and localizes the wording; the client only renders it.
  frequencyLabel: mention.mentionFrequency,
  // Single wire excerpt → the domain's quote list (empty when absent, not a throw).
  quotes: mention.representativeExcerpt ? [toQuote(mention.representativeExcerpt)] : [],
  sentiment,
});

/**
 * Wire → domain.
 *
 * The backend wraps the insight in `{ reviewInsight }` and collapses every unavailable state into
 * `null` (no `availability` field). Per the product decision, a null insight always renders the
 * "gathering reviews" card, so it maps to `processing`. A present insight is only treated as
 * `ready` when it carries a numeric `averageRating`; without one, the review-summary card would
 * show a misleading "0.0", so it also falls back to `processing` (still gathering).
 *
 * The 979 review-summary design uses only the rating, the per-group summaries (positive/caution),
 * and the mention groups. `summary`/`attributes`/`recommendedPercent` — which the backend does not
 * send and no component reads — are filled with empty values to satisfy the shared domain shape.
 */
export const convertReviewInsightToWhatPeopleSayInsight = (
  response: GetProductReviewInsightResponse,
): WhatPeopleSayInsight => {
  const insight = response.reviewInsight;

  // A null insight, or a present insight with no numeric rating yet, is still "gathering".
  if (!insight || insight.averageRating == null) {
    return { availability: 'processing', data: null };
  }

  const { reviewCount, summary, mentions } = insight;

  const data: WhatPeopleSay = {
    rating: {
      score: insight.averageRating,
      reviewCount,
      recommendedPercent: 0,
    },
    summary: { text: '', sources: [] },
    // Defensive: a malformed payload with a missing nested array degrades to empty, not a throw.
    loved: (mentions?.positive ?? []).map((mention) => toMention(mention, 'positive')),
    improvements: (mentions?.caution ?? []).map((mention) => toMention(mention, 'negative')),
    attributes: [],
    lovedSummary: summary?.positiveText,
    improvementsSummary: summary?.cautionText,
  };

  return { availability: 'ready', data };
};
